import { Node, mergeAttributes } from "@tiptap/core";

import { LINK_EXTENSION_PRIORITY, DEFAULT_PARSE_RULE_PRIORITY } from "../constants";

import type { Node as ProsemirrorNode } from "@tiptap/pm/model";

export const MENTION_NODE_NAME = "mention";

export type MentionNodeAttrs = {
  id: string;
  userId: string;
  /** Denormalised so a mention renders without a lookup; may be stale. */
  username: string;
  /**
   * Who inserted the mention.
   */
  authorId: string;
};

export function nodeIsMention(node?: ProsemirrorNode): boolean {
  return node?.type.name === MENTION_NODE_NAME;
}

export function getMentionText(node: ProsemirrorNode): string {
  const { username } = node.attrs as MentionNodeAttrs;
  return username ? `@${username}` : "";
}

/**
 * @param alias secondary HTML attribute, read as a parse fallback and written alongside
 *   `htmlName`, so mentions interoperate with `@tiptap/extension-mention`'s `data-id` /
 *   `data-label` shape in both directions.
 */
function dataAttribute(name: keyof MentionNodeAttrs, htmlName: string) {
  return {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute(htmlName),
    renderHTML: (attributes: Record<string, string | null>) => {
      const value = attributes[name];
      if (!value) return {};
      return { [htmlName]: value };
    },
  };
}

export const Mention = Node.create({
  name: MENTION_NODE_NAME,

  group: "inline",

  atom: true,

  inline: true,

  selectable: false,

  priority: LINK_EXTENSION_PRIORITY + 1,

  addAttributes() {
    return {
      id: dataAttribute("id", "id"),
      userId: dataAttribute("userId", "data-user-id"),
      username: dataAttribute("username", "data-username"),
      authorId: dataAttribute("authorId", "data-author-id"),
    };
  },

  extendNodeSchema(extension) {
    if (extension.name !== MENTION_NODE_NAME) return {};
    return { leafText: getMentionText };
  },

  renderText({ node }) {
    return getMentionText(node);
  },

  parseHTML() {
    // At equal priority ProseMirror inserts every mark rule ahead of the node rules, and Link
    // claims any `a` carrying an href — which a rendered mention does.
    const priority = DEFAULT_PARSE_RULE_PRIORITY + 1;

    return [{ tag: `a[data-type="${MENTION_NODE_NAME}"]`, priority }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { username } = node.attrs as MentionNodeAttrs;

    return [
      "a",
      mergeAttributes(
        { "data-type": MENTION_NODE_NAME, ...(username && { href: `/${username}` }) },
        HTMLAttributes,
      ),
      getMentionText(node),
    ];
  },
});
